<template>
    <Modal ref="modal" :title="$t('labels.import_from_external_resource')" size="full" @onHide="hide">
        <div class="modal-body">
            <template v-if="!fetchTask.state.isRunning && !category.source_id && sourceFilters.length === 0">
                <FormGroup :label="$t('labels.category')">
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/marketplace/sources/${integration.service}/categories`"
                        @update:model-value="marketplaceCategory = $event"
                        :model-value="marketplaceCategory?.id"
                        :selected-value="marketplaceCategory?.name_uk"
                        option-key="id"
                        option-value="name_uk"
                        :query-params="{
                            has_child: 0,
                            orders: [
                                {column : 'name_uk', dir : 'asc'}
                            ]
                        }"
                    />
                </FormGroup>
                <template v-if="categoryErrorMessage">
                    <Alert type="warning">
                        {{ categoryErrorMessage }}
                    </Alert>
                </template>
            </template>
            <template v-else-if="fetchTask.state.isRunning">
                <TaskProgressBar :done="fetchTask.state.done" :total="fetchTask.state.total"/>
            </template>
            <template v-else-if="!importTask.state.isRunning && badHttpHistory.length">
                <div class="table-responsive table-scrollable">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_request') }}</th>
                                <th>{{ $t('labels.copy_products_response') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="badHttpHistoryItem in badHttpHistory">
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.request.body }}</span></td>
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.response.body }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else-if="!importTask.state.isRunning">
                <Alert type="warning" v-if="messageFromMarketPlaceError">
                    {{ messageFromMarketPlaceError }}
                </Alert>
                <div class="mb-3">
                    <AppButton size="sm" color="light" class="mr-2" @click="expandedFilters = sourceFilters.map(filter => filter.id)">
                        <i class="icon-enlarge7 mr-2"></i>
                        {{ $t('buttons.expand_all') }}
                    </AppButton>
                    <AppButton size="sm" color="light" class="mr-2" @click="expandedFilters = []">
                        <i class="icon-shrink7 mr-2"></i>
                        {{ $t('buttons.collapse_all') }}
                    </AppButton>
                    <AppButton color="light" @click="deleteSelectedValues" :disabled="!Object.keys(selectedValues).length">
                        <i class="icon-trash mr-2"></i>
                        {{ $t('buttons.delete') }}
                    </AppButton>
                </div>
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="60px">
                                <AppCheckbox :model-value="selectedValueIds.length === totalValuesCount" @update:modelValue="checkAllValues"/>
                            </th>
                            <th width="110px">
                                <div class="d-flex justify-content-center">
                                    <span class="badge badge-flat badge-pill border-success text-success-600 mr-2">{{ totalExistingValues }}</span>
                                    <span class="badge badge-flat badge-pill border-grey-800 text-default">{{ totalNewValues }}</span>
                                </div>
                            </th>
                            <th width="150px">{{ $t('table.titles.status_in_catalog') }}</th>
                            <th>{{ $t('table.titles.name') }}</th>
                            <th>{{ $t('table.titles.type') }}</th>
                            <th>{{ $t('table.titles.measurement') }}</th>
                            <th>{{ $t('table.titles.required') }}</th>
                            <th>{{ $t('table.titles.allow_custom_values') }}</th>
                            <th>{{ $t('table.titles.is_multiple') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(filter, filterIndex) in sourceFilters">
                            <tr class="filter-row">
                                <td>
                                    <AppCheckbox
                                        :model-value="selectedValueIds.includes(`filter-${filter.id}`)"
                                        @change="checkFilter(filter)"
                                    />
                                </td>
                                <td>
                                    <div class="d-flex justify-content-center" v-if="filter.hasTooManyItems">
                                        <span  class="badge badge-flat badge-pill text-danger border-danger">{{ $t('alerts.tooManyItems') }}</span>
                                    </div>
                                    <template v-if="filter.filterItems.length">
                                        <div class="d-flex justify-content-center">
                                            <span class="badge badge-flat badge-pill border-success text-success-600 mr-2">{{ calculateExistingValuesCountInRow(filter) }}</span>
                                            <span class="badge badge-flat badge-pill border-grey-800 text-default">{{ calculateNewValuesCountInRow(filter) }}</span>
                                        </div>
                                    </template>
                                </td>
                                <td>
                                    <span :class="['badge badge-flat badge-pill', filter.existsFilterUuid === null ? 'border-grey-800 text-default' : 'border-success text-success-600']">
                                        {{ filter.existsFilterUuid === null ? $t('labels.new') : $t('labels.existing') }}
                                    </span>
                                </td>
                                <td>
                                    <i
                                        @click="toggleFilterExpansion(filter.id)"
                                        :class="[expandedFilters.includes(filter.id) ? 'icon-arrow-up12' : 'icon-arrow-down12']"
                                        :style="{ visibility: filter.filterItems.length ? 'visible' : 'hidden' }"
                                    ></i>
                                    {{ filter.names[mainLanguage.alias] }}
                                </td>
                                <td>
                                    {{ $t(`filter_types.${filter.type}`) }}
                                    <span v-if="filter.existsFilterUuid !== null && filter.type !== findExistingFilter(filter).type" class="text-muted">
                                         ({{ $t(`filter_types.${findExistingFilter(filter).type}`) }})
                                    </span>
                                    <br/>
                                    <span v-if="filter.type === 'number'" class="text-muted text-smaller">
                                        <span v-if="filter.minNumberValue !== null">
                                            {{ $t('labels.min') }}: {{ filter.minNumberValue }}
                                        </span>
                                        <span v-if="filter.maxNumberValue !== null">
                                            <span v-if="filter.minNumberValue !== null"> - </span>
                                            {{ $t('labels.max') }}: {{ filter.maxNumberValue }}
                                        </span>
                                    </span>
                                </td>
                                <td>
                                    <AppNullableCell>
                                        <template v-if="filter.measurementUnit" #default>
                                            {{ filter.measurementUnit }}
                                        </template>
                                    </AppNullableCell>
                                </td>
                                <td>
                                    {{ filter.isRequired ? $t('labels.yes') : $t('labels.no') }}
                                    <span v-if="filter.existsFilterUuid !== null && filter.isRequired != findExistingFilter(filter).is_required" class="text-muted">
                                         ({{ findExistingFilter(filter).is_required ? $t('labels.yes') : $t('labels.no') }})
                                    </span>
                                </td>
                                <td>
                                    {{ filter.allowCustomValues ? $t('labels.yes') : $t('labels.no') }}
                                    <span v-if="filter.existsFilterUuid !== null && filter.allowCustomValues != findExistingFilter(filter).allow_custom_values" class="text-muted">
                                         ({{ findExistingFilter(filter).allow_custom_values ? $t('labels.yes') : $t('labels.no') }})
                                    </span>
                                </td>
                                <td>
                                    {{ filter.is_multiple ? $t('labels.yes') : $t('labels.no') }}
                                    <span v-if="filter.existsFilterUuid !== null && filter.is_multiple != findExistingFilter(filter).is_multiple" class="text-muted">
                                         ({{ findExistingFilter(filter).is_multiple ? $t('labels.yes') : $t('labels.no') }})
                                    </span>
                                </td>
                            </tr>
                            <template v-for="item in filter.filterItems" v-if="expandedFilters.includes(filter.id)">
                                <tr>
                                    <td>
                                        <AppCheckbox :model-value="selectedValueIds.includes(`filter-item-${item.id}`)" @change="checkFilterItem(filter, item)"/>
                                    </td>
                                    <td></td>
                                    <td>
                                        <span :class="['badge badge-flat badge-pill', isNewFilterItem(filter, item) ? 'border-grey-800 text-default' : 'border-success text-success-600']">
                                            {{ isNewFilterItem(filter, item) ? $t('labels.new') : $t('labels.existing') }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="ml-4">{{ item.names[mainLanguage.alias] === "" || item.names[mainLanguage.alias] === undefined ? Object.values(item.names)[0] : item.names[mainLanguage.alias] }}</span>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                </tr>
                            </template>
                        </template>
                    </tbody>
                </table>
                <AppFieldset :title="$t('labels.fields_for_update')">
                    <div class="row">
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.all')">
                                <AppCheckbox :model-value="allFieldsSelected" @update:model-value="toggleAllFields" /> 
                            </FormGroup>
                        </div>
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.type')">
                                <AppCheckbox v-model="fieldsForUpdate.type"/>
                            </FormGroup>
                        </div>
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.required')">
                                <AppCheckbox v-model="fieldsForUpdate.is_required"/>
                            </FormGroup>
                        </div>
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.custom_values')">
                                <AppCheckbox v-model="fieldsForUpdate.allow_custom_values"/>
                            </FormGroup>
                        </div>
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.is_multiple')">
                                <AppCheckbox v-model="fieldsForUpdate.is_multiple"/>
                            </FormGroup>
                        </div>
                        <div class="col-md-2">
                            <FormGroup :label="$t('labels.measurement')">
                                <AppCheckbox v-model="fieldsForUpdate.measurementUnit"/>
                            </FormGroup>
                        </div>
                    </div>
                </AppFieldset>
            </template>
            <template v-else>
                <TaskProgressBar :done="importTask.state.done" :total="importTask.state.total"/>
            </template>
        </div>
        <div class="modal-footer" v-if="shouldShowModalFooter">
            <AppButton @click="fetchSourceFilters(marketplaceCategory.source_id)" color="indigo" v-if="sourceFilters.length === 0" :disabled="!marketplaceCategory">
                {{ $t("buttons.upload") }}
            </AppButton>
            <AppButton @click="importFiltersAndItems" :disabled="importTask.state.isRunning" color="indigo" v-else>
                {{ $t("buttons.upload") }}
            </AppButton>
        </div>
    </Modal>
</template>
<script>
import Modal from "../../../../components/new/modal/AppModal.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import AppNullableCell from "../../../components/tables/AppNullableCell.vue";
import HttpSender from "../../../../services/Http/HttpSender";
import HttpRequest from "../../../../services/Http/HttpRequest";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import TaskProgressBar from "../../../../components/new/progress/TaskProgressBar.vue";
import AppFieldset from "../../../../components/new/AppFieldset.vue";
import AppAutoComplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import Alert from "../../../../components/AlertComponent.vue";

import { httpClient } from "../../../../services/HttpClient";
import { getMessageFromMarketPlaceError } from "../../../../utils";
import { createProgressTask } from "../../../../services/Progress/ProgressTask";
import { IntegrationServiceEnum  } from "../../../enums/IntegrationServiceEnum";

export default {
    name: "CreateFiltersFromExternalResourceModal",
    components: {
        Modal, FormGroup, Input, AppButton, AppNullableCell, AppCheckbox, TaskProgressBar, AppFieldset,
        AppAutoComplete, Alert
    },
    props: {
        integration: {
            type: Object
        },
        category: {
            type: Object
        }
    },
    emits: ['filtersCreated'],
    data() {
        return {
            IntegrationServiceEnum,
            sourceFilters: [],
            existingFilters: {},
            existingFiltersBySourceId: {},
            matchedExistingFilterByFilterId: {},
            expandedFilters: [],
            selectedValues: {},
            mainLanguage: null,
            importTask: createProgressTask(),
            fetchTask: createProgressTask(),
            fieldsForUpdate: {
                type: false,
                is_required: false,
                allow_custom_values: false,
                is_multiple: false,
                measurementUnit: false
            },
            badHttpHistory: [],
            httpSender: new HttpSender(),
            marketplaceCategory: null,
            categoryErrorMessage: null,
            messageFromMarketPlaceError: null
        };
    },
    methods: {
        async show() {
            await this.fetchMainLanguage();
            this.$refs.modal.show();

            if (this.category.source_id) {
                this.$nextTick(() => {
                    this.fetchSourceFilters(this.category.source_id);
                })
            }

        },
        async fetchExistingFilters() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.$route.params.categoryUuid],
                extra: 1,
                limit: 0
            }).then((response) => {
                response.data.data.forEach((filter) => {
                    const existingFilter = {
                        uuid: filter.uuid,
                        type: filter.type,
                        is_required: filter.is_required,
                        allow_custom_values: filter.allow_custom_values,
                        is_multiple: filter.is_multiple,
                        measurementUnit: filter.measurement_unit ?? null,
                        itemsByName: {},
                        itemsBySourceId: {}
                    };

                    const nameKey = this.buildFilterNameKey(filter.names[this.mainLanguage.alias], existingFilter.measurementUnit);
                    this.existingFilters[nameKey] = existingFilter;

                    if (filter.source_id) {
                        this.existingFiltersBySourceId[filter.source_id] = existingFilter;
                    }

                    this.fetchExistingFilterItems(filter.uuid, existingFilter);
                });
            });
        },
        async fetchExistingFilterItems(integrationFilterUuid, existingFilter) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                integration_filter_uuids: [integrationFilterUuid],
                extra: 1,
                limit: 0
            }).then((response) => {
                response.data.data.forEach((item) => {
                    const itemName = item.names[this.mainLanguage.alias] ?? Object.values(item.names)[0];
                    existingFilter.itemsByName[itemName] = item;

                    if (item.source_id) {
                        existingFilter.itemsBySourceId[item.source_id] = item;
                    }
                });
            });
        },
        findExistingFilter(filter) {
            return this.matchedExistingFilterByFilterId[filter.id] ?? null;
        },
        buildExistingFilterMatches(sourceFilters) {
            const matched = {};
            const usedFilterUuids = new Set();

            // First pass: mark filters already imported (matched by source_id) as existing.
            sourceFilters.forEach((filter) => {
                const existingBySourceId = this.existingFiltersBySourceId[filter.id];
                if (existingBySourceId && !usedFilterUuids.has(existingBySourceId.uuid)) {
                    matched[filter.id] = existingBySourceId;
                    usedFilterUuids.add(existingBySourceId.uuid);
                }
            });

            // Second pass: match the remaining filters by name + measurement unit,
            // never reusing an existing filter already claimed by source_id.
            sourceFilters.forEach((filter) => {
                if (matched[filter.id]) {
                    return;
                }

                for (const name of Object.values(filter.names)) {
                    const nameKey = this.buildFilterNameKey(name, filter.measurementUnit ?? null);
                    const existingByName = this.existingFilters[nameKey];
                    if (existingByName && !usedFilterUuids.has(existingByName.uuid)) {
                        matched[filter.id] = existingByName;
                        usedFilterUuids.add(existingByName.uuid);
                        break;
                    }
                }
            });

            return matched;
        },
        buildFilterNameKey(name, measurementUnit) {
            const nameKey = `${name}|${measurementUnit ?? ''}`;

            return nameKey;
        },
        findExistingFilterItem(existingFilter, item) {
            if (item.id && existingFilter.itemsBySourceId[item.id]) {
                return existingFilter.itemsBySourceId[item.id];
            }

            for (const name of Object.values(item.names)) {
                if (existingFilter.itemsByName[name]) {
                    return existingFilter.itemsByName[name];
                }
            }

            return null;
        },
        hide() {
            this.importTask.cancel();
            this.fetchTask.cancel();
            this.$refs.modal.hide();
            this.sourceFilters = [];
            this.existingFilters = {};
            this.existingFiltersBySourceId = {};
            this.matchedExistingFilterByFilterId = {};
            this.expandedFilters = [];
            this.selectedValues = {};
            this.mainLanguage = null;
            this.fieldsForUpdate = {
                type: false,
                is_required: false,
                allow_custom_values: false,
                is_multiple: false
            };
            this.badHttpHistory = [];
            this.httpSender = new HttpSender();
            this.categoryErrorMessage = null;
            this.messageFromMarketPlaceError = null;
        },
        async fetchSourceFilters(categorySourceId) {
            this.categoryErrorMessage = null;
            this.messageFromMarketPlaceError = null;

            await this.fetchExistingFilters();

            await this.fetchTask.run(async ({signal, tick}) => {
                let sourceFilters = await this.fetchFromApi(categorySourceId, tick);

                if (signal.aborted) {
                    return;
                }

                this.sourceFilters = this.sortFiltersAndFilterItemsByName(sourceFilters);
                this.matchedExistingFilterByFilterId = this.buildExistingFilterMatches(this.sourceFilters);
                this.sourceFilters.forEach((filter) => {
                    filter.existsFilterUuid = this.matchedExistingFilterByFilterId[filter.id]?.uuid ?? null;
                });
            });
        },
        sortFiltersAndFilterItemsByName(sourceFilters) {
            let sortedFilters = [...sourceFilters];

            sortedFilters.sort((a, b) => {
                const nameA = a.names[this.mainLanguage.alias] || Object.values(a.names)[0];
                const nameB = b.names[this.mainLanguage.alias] || Object.values(b.names)[0];
                return nameA.localeCompare(nameB);
            });

            sortedFilters.forEach(filter => {
                filter.filterItems.sort((a, b) => {
                    const nameA = a.names[this.mainLanguage.alias] || Object.values(a.names)[0];
                    const nameB = b.names[this.mainLanguage.alias] || Object.values(b.names)[0];
                    return nameA.localeCompare(nameB);
                });
            });

            return sortedFilters;
        },
        getCategorySourceId(categorySourceId) {
            if (!categorySourceId) {
                return null;
            }

            if (String(categorySourceId).includes('-')) {
                return Number(String(categorySourceId).split('-')[0]);
            }

            return categorySourceId;
        },
        async fetchFromApi(categorySourceId, tick) {
            let result = new Map();

            try {
                const attributesUrl = `/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integration.service}/categories/${this.getCategorySourceId(categorySourceId)}/attributes`;
                const attributesLimit = 100;
                const filters = [];

                // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                const firstAttributesResponse = await httpClient.get(attributesUrl, { page: 1, limit: attributesLimit }, false, undefined, { skipNotFoundRedirect: true });
                filters.push(...firstAttributesResponse.data.data);

                const attributesTotalPages = Math.ceil(firstAttributesResponse.data.meta.total / attributesLimit);
                for (let page = 2; page <= attributesTotalPages; page++) {
                    // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                    const response = await httpClient.get(attributesUrl, { page, limit: attributesLimit }, false, undefined, { skipNotFoundRedirect: true });
                    filters.push(...response.data.data);
                }

                tick();
                filters.forEach(filter => {
                    const names = {};
                    this.integration.language_aliases.forEach(lang => {
                        if (filter[`name_${lang}`]) {
                            names[lang] = filter[`name_${lang}`];
                        }
                    });

                    result.set(filter.source_id, {
                        id: filter.source_id,
                        names: names,
                        isRequired: filter.is_required,
                        allowCustomValues: filter.allow_custom_values,
                        minNumberValue: filter.min_number_value || null,
                        maxNumberValue: filter.max_number_value || null,
                        type: filter.type,
                        filterItems: [],
                        existsFilterUuid: null,
                        is_multiple: filter.is_multiple,
                        hasTooManyItems: false,
                        measurementUnit: filter.measurement_unit || null
                    });
                });
            } catch (error) {
                const messageFromMarketPlaceError = getMessageFromMarketPlaceError(error);
                if (messageFromMarketPlaceError) {
                    this.categoryErrorMessage = messageFromMarketPlaceError;
                    return [];
                }
                throw error;
            }

            for (let [id, filter] of result.entries()) {
                if (filter.type === 'text') {
                    continue;
                }

                let queryParams = {
                    category_source_id: this.category.source_id
                };

                if (this.category?.parent_source_id) {
                    queryParams.category_parent_id = this.category.parent_source_id;
                }

                const itemsUrl = `/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integration.service}/categories/${this.getCategorySourceId(categorySourceId)}/attributes/${id}/items`;

                try {
                    // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                    const firstResponse = await httpClient.get(itemsUrl, { ...queryParams, page: 1, limit: 100 }, false, undefined, { skipNotFoundRedirect: true });
                    tick();

                    if (firstResponse.data.meta.total > 300) {
                        filter.hasTooManyItems = true;
                    } else {
                        const items = [...firstResponse.data.data];
                        const totalPages = Math.ceil(firstResponse.data.meta.total / 100);

                        for (let page = 2; page <= totalPages; page++) {
                            // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                            const response = await httpClient.get(itemsUrl, { ...queryParams, page, limit: 100 }, false, undefined, { skipNotFoundRedirect: true });
                            tick();
                            items.push(...response.data.data);
                        }

                        items.forEach(item => {
                            const names = {};
                            this.integration.language_aliases.forEach(lang => {
                                if (item[`name_${lang}`]) {
                                    names[lang] = item[`name_${lang}`];
                                }
                            });

                            filter.filterItems.push({
                                id: item.source_id,
                                names: names
                            });
                        })
                    }
                } catch (error) {
                    const messageFromMarketPlaceError = getMessageFromMarketPlaceError(error);

                    if (messageFromMarketPlaceError) {
                        if (error.response.data.message.includes('MarketplaceAttribute')) {
                            this.messageFromMarketPlaceError = messageFromMarketPlaceError;
                        } else {
                            this.categoryErrorMessage = messageFromMarketPlaceError;
                        }
                        continue;
                    }
                    
                    throw error;
                }

                result.set(id, filter);
            }

            result = Array.from(result.values());

            return result;
        },
        async importFiltersAndItems() {
            const completed = await this.importTask.run(async ({tick}) => {
                let newFilterAndFilterItems = [];
                let newFilterItemsAfterCreateFilters = [];

                this.sourceFilters.forEach((filter) => {
                    if (!this.selectedValues.hasOwnProperty(`filter-${filter.id}`)) return;
                    
                    if (filter.existsFilterUuid === null) {
                        let requestBody = {
                            integration_category_uuid: this.$route.params.categoryUuid,
                            names: filter.names,
                            type: filter.type,
                            is_required: filter.isRequired,
                            allow_custom_values: filter.allowCustomValues,
                            is_multiple: filter.is_multiple,
                            measurement_unit: filter.measurementUnit
                        }

                        if (filter.type === 'number') {
                            requestBody.min_number_value = filter.minNumberValue;
                            requestBody.max_number_value = filter.maxNumberValue;
                        }

                        if (filter?.id) {
                            requestBody.source_id = filter.id;
                        }

                        newFilterAndFilterItems.push(new HttpRequest("POST", `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, requestBody));
                    } else {
                        let requestBody = {
                            names: filter.names
                        }

                        if (this.fieldsForUpdate.type) {
                            requestBody.type = filter.type;
                        }

                        if (this.fieldsForUpdate.is_required) {
                            requestBody.is_required = filter.isRequired;
                        }

                        if (this.fieldsForUpdate.allow_custom_values) {
                            requestBody.allow_custom_values = filter.allowCustomValues;
                        }

                        if (this.fieldsForUpdate.is_multiple) {
                            requestBody.is_multiple = filter.is_multiple;
                        }

                        if (this.fieldsForUpdate.measurementUnit) {
                            requestBody.measurement_unit = filter.measurementUnit;
                        }

                        if (filter?.id) {
                            requestBody.source_id = filter.id;
                        }

                        newFilterAndFilterItems.push(new HttpRequest("PATCH", `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters/${filter.existsFilterUuid}`, requestBody));
                    }

                    filter.filterItems.forEach(item => {
                        if (!this.selectedValues.hasOwnProperty(`filter-item-${item.id}`)) return;

                        if (this.isNewFilterItem(filter, item) && filter.existsFilterUuid !== null) {
                            newFilterAndFilterItems.push(new HttpRequest("POST", `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                                source_id: item.id,
                                integration_filter_uuid: filter.existsFilterUuid,
                                names: item.names
                            }));
                        } else {
                            const existingFilter = this.findExistingFilter(filter);
                            const existingItem = existingFilter ? this.findExistingFilterItem(existingFilter, item) : null;

                            if (!existingItem) return;

                            newFilterAndFilterItems.push(new HttpRequest("PATCH", `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items/${existingItem.uuid}`, {
                                source_id: item.id,
                                names: item.names
                            }));
                        }
                    });
                })

                await this.httpSender.sendRequestsInBatches(newFilterAndFilterItems, (request, response, index) => {
                    tick();
                    if (response.status === 201 && request.path === `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`) {
                        this.selectedValues[`filter-${response.body.source_id}`].filterItems.forEach(item => {
                            if (!this.selectedValues.hasOwnProperty(`filter-item-${item.id}`)) return;

                            if (this.isNewFilterItem(this.selectedValues[`filter-${response.body.source_id}`], item)) {
                                newFilterItemsAfterCreateFilters.push(new HttpRequest("POST", `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                                    source_id: item.id,
                                    integration_filter_uuid: response.body.uuid,
                                    names: item.names
                                }));
                            }
                        })
                    }
                });

                await this.httpSender.sendRequestsInBatches(newFilterItemsAfterCreateFilters, (request, response, index) => {
                    tick();
                });

                this.badHttpHistory = this.httpSender.getHistory().filter((historyItem) => {
                    return historyItem.response.status !== 201 && historyItem.response.status !== 200;
                });
            });

            if (!completed) {
                return;
            }

            this.$emit('filtersCreated');

            if (this.badHttpHistory.length === 0) {
                this.hide();
            }
        },
        isNewFilterItem(filter, item) {
            const existingFilter = this.findExistingFilter(filter);

            if (!existingFilter) {
                return true;
            }

            return this.findExistingFilterItem(existingFilter, item) === null;
        },
        toggleFilterExpansion(filterId) {
            const index = this.expandedFilters.indexOf(filterId);
            if (index > -1) {
                this.expandedFilters.splice(index, 1);
            } else {
                this.expandedFilters.push(filterId);
            }
        },
        async fetchMainLanguage() {
            await httpClient.get('/customization/languages/list').then((response) => {
                this.mainLanguage = response.data.data.find(lang => lang.main);
            })
        },
        calculateNewValuesCountInRow(filter) {
            let newCount = filter.filterItems.reduce((count, item) => {
                return count + (this.isNewFilterItem(filter, item) ? 1 : 0);
            }, 0);

            return newCount;
        },
        calculateExistingValuesCountInRow(filter) {
            let existingCount = filter.filterItems.reduce((count, item) => {
                return count + (this.isNewFilterItem(filter, item) ? 0 : 1);
            }, 0);

            return existingCount;
        },
        checkFilter(filter) {
            if (!this.selectedValues.hasOwnProperty(`filter-${filter.id}`)) {
                this.selectedValues[`filter-${filter.id}`] = filter;
                filter.filterItems.forEach(item => {
                    this.selectedValues[`filter-item-${item.id}`] = filter;
                });
            } else {
                delete this.selectedValues[`filter-${filter.id}`];
                filter.filterItems.forEach(item => {
                    delete this.selectedValues[`filter-item-${item.id}`];
                });
            }
        },
        checkFilterItem(filter, filterItem) {
            if (!this.selectedValues.hasOwnProperty(`filter-item-${filterItem.id}`)) {
                if (!this.selectedValues.hasOwnProperty(`filter-${filter.id}`)) {
                    this.selectedValues[`filter-${filter.id}`] = filter;
                }
                this.selectedValues[`filter-item-${filterItem.id}`] = null;
            } else {
                delete this.selectedValues[`filter-item-${filterItem.id}`];
            }
        },
        checkAllValues(check) {
            if (check) {
                this.sourceFilters.forEach((filter) => {
                    if (!this.selectedValues.hasOwnProperty(filter.id)) {
                        this.selectedValues[`filter-${filter.id}`] = filter;
                    }
                    filter.filterItems.forEach(item => {
                        if (!this.selectedValues.hasOwnProperty(item.id)) {
                            this.selectedValues[`filter-item-${item.id}`] = null;
                        }
                    });
                });
            } else {
                this.selectedValues = {};
            }
        },
        deleteSelectedValues() {
            this.sourceFilters = this.sourceFilters.filter((filter) => {
                if (this.selectedValueIds.includes(filter.id)) {
                    return false;
                }
                filter.filterItems = filter.filterItems.filter(item => !this.selectedValueIds.includes(item.id));
                return true;
            });
            this.selectedValues = {};
        },
        toggleAllFields(value) {
            for (const key in this.fieldsForUpdate) {
                this.fieldsForUpdate[key] = value;
            }
        }
    },
    computed: {
        allFieldsSelected() {
            return Object.values(this.fieldsForUpdate).every(value => value === true);
        },
        totalValuesCount() {
            let total = 0;
            this.sourceFilters.forEach(filter => {
                total++;
                total += filter.filterItems.length;
            });
            return total;
        },
        totalExistingValues() {
            return this.sourceFilters.reduce((total, filter) => {
                filter.filterItems.forEach(item => {
                    if (!this.isNewFilterItem(filter, item)) {
                        total++;
                    }
                });

                return total;
            }, 0);
        },
        totalNewValues() {
            return this.sourceFilters.reduce((total, filter) => {
                filter.filterItems.forEach(item => {
                    if (this.isNewFilterItem(filter, item)) {
                        total++;
                    }
                });

                return total;
            }, 0);
        },
        selectedValueIds() {
            return Object.keys(this.selectedValues);
        },
        shouldShowModalFooter() {
            return this.badHttpHistory.length === 0 && !this.fetchTask.state.isRunning && !this.categoryErrorMessage;
        }
    },
    beforeUnmount() {
        this.importTask.cancel();
        this.fetchTask.cancel();
    }
}
</script>
